import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import fileDownload from "js-file-download";
import moment from "moment";
import strings from "../global/strings";
import { formatCustomerIdParam } from "../global/customer-id.util";

export type ExportTableName = "bill" | "customer" | "activity" | "user" | "all";

export interface ExportFileVars {
	tableName: ExportTableName;
	filterValues?: Record<string, unknown>;
	companyId?: string | number;
}

// Same per-table querystring shaping the old export-import.action.js thunk
// used (bill/customer date+id massaging, activity month range; user/all send
// none). Kept as a manually-built querystring (not axios `params`) to match
// the exact key set/ordering the BE currently expects; not something this
// migration is meant to change.
function buildExportQuery(
	tableName: ExportTableName,
	filterValues: Record<string, unknown>,
): string {
	switch (tableName) {
		case "bill": {
			const bill_void =
				filterValues.bill_void === true || filterValues.bill_void === false
					? filterValues.bill_void
					: "";
			const date_from =
				filterValues.fiscal_year === "" ? filterValues.date_from : "";
			const date_to =
				filterValues.fiscal_year === "" ? filterValues.date_to : "";
			const customer_id = formatCustomerIdParam(filterValues.customer_id as string);
			return `date_from=${date_from}&date_to=${date_to}&buyer_name=${filterValues.buyer_name}&buyer_pan=${filterValues.buyer_pan}&invoice_number=${filterValues.invoice_number}&total_sales_min=${filterValues.total_sales_min}&total_sales_max=${filterValues.total_sales_max}&fiscal_year=${filterValues.fiscal_year}&bill_void=${bill_void}&customer_id=${customer_id}`;
		}
		case "customer": {
			const customer_id = formatCustomerIdParam(filterValues.customer_id as string);
			return `customer_id=${customer_id}&customer_name=${filterValues.customer_name}&customer_gender=${filterValues.customer_gender}&customer_type=${filterValues.customer_type}&customer_pan_vat=${filterValues.customer_pan_vat}&customer_mobile=${filterValues.customer_mobile}&customer_email=${filterValues.customer_email}`;
		}
		case "activity":
			return `startMonth=${filterValues.startMonth}&endMonth=${filterValues.endMonth}`;
		case "user":
		case "all":
		default:
			return "";
	}
}

async function postExportFile({
	tableName,
	filterValues = {},
	companyId,
}: ExportFileVars): Promise<void> {
	const queryString = buildExportQuery(tableName, filterValues);

	const res = await axios.get(
		`${strings.url}multer/file/download?tableName=${tableName}&${queryString}`,
		{
			responseType: "arraybuffer",
			withCredentials: true,
		},
	);

	// mirrors the old thunk's filename quirk: "activity" downloads are
	// still named "preference" on disk
	const filename = tableName === "activity" ? "preference" : tableName;

	fileDownload(
		res.data,
		`BillMe-${companyId}-${filename}_${moment().format("YYYY-MM-DD")}.xlsx`,
	);
}

// No toast — callers render their own FormError/InlineNotice bound to this
// mutation's isError/isSuccess (see bill-filter/employee-list/customer-list/
// company-setting/activity-monitor).
export function useExportFile() {
	return useMutation({
		mutationFn: postExportFile,
	});
}
